export const TRUNK_NAME_MAX_LENGTH = 40;

export const TRUNK_NAME_CODES = {
  INVALID_CARRIER: 'INVALID_CARRIER',
  MISSING_SHORT_NAME: 'MISSING_SHORT_NAME',
  TOO_LONG: 'TOO_LONG',
};

export const TRUNK_NAME_MESSAGES = {
  [TRUNK_NAME_CODES.INVALID_CARRIER]: 'Operadora com nome inválido.',
  [TRUNK_NAME_CODES.MISSING_SHORT_NAME]: 'Tenant sem nome curto cadastrado',
  [TRUNK_NAME_CODES.TOO_LONG]: `Nome do tronco excede ${TRUNK_NAME_MAX_LENGTH} caracteres. Use uma operadora com nome mais curto.`,
};

const SEPARATORS_AROUND_TENANT = 2;

const failure = (code) => ({ ok: false, code });

const isNumericName = (name) => !Number.isNaN(Number(name));

export const normalizeTrunkSegment = (text) => String(text ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]/g, '');

export const carrierPrefix = (trunkName) => String(trunkName ?? '').split('-')[0].trim().toLowerCase();

export const trunkNamesOf = (endpoints) => {
  if (!Array.isArray(endpoints)) return [];
  return endpoints
    .map((item) => (item && typeof item.endpoint === 'string' ? item.endpoint.trim() : ''))
    .filter((name) => name !== '' && !isNumericName(name));
};

export const buildTrunkName = ({ carrierName, tenant = {}, existingNames = [], batchLastNumber = null, batch = false }) => {
  const carrierSegment = normalizeTrunkSegment(carrierName);
  if (!carrierSegment) return failure(TRUNK_NAME_CODES.INVALID_CARRIER);

  const safeTenant = tenant || {};
  const shortNameSegment = normalizeTrunkSegment(safeTenant.short_name);
  if (!shortNameSegment) return failure(TRUNK_NAME_CODES.MISSING_SHORT_NAME);

  const tenantSegment = normalizeTrunkSegment(safeTenant.name) || shortNameSegment;
  const batchLength = batchLastNumber ? 1 + String(batchLastNumber).length : 0;

  const compose = (attempt) => {
    const suffix = attempt > 1 ? `-${attempt}` : '';
    const room = TRUNK_NAME_MAX_LENGTH
      - (carrierSegment.length + SEPARATORS_AROUND_TENANT + shortNameSegment.length + suffix.length + batchLength);
    if (room < 1) return null;
    return `${carrierSegment}-${tenantSegment.slice(0, room)}-${shortNameSegment}${suffix}`;
  };

  if (batch || batchLastNumber) {
    const base = compose(1);
    return base ? { ok: true, value: base } : failure(TRUNK_NAME_CODES.TOO_LONG);
  }

  const taken = new Set(existingNames.map((name) => String(name).toLowerCase()));
  for (let attempt = 1; attempt <= taken.size + 1; attempt += 1) {
    const candidate = compose(attempt);
    if (candidate === null) return failure(TRUNK_NAME_CODES.TOO_LONG);
    if (!taken.has(candidate)) return { ok: true, value: candidate };
  }
  return failure(TRUNK_NAME_CODES.TOO_LONG);
};

export const extractCarriers = (names) => {
  const carriers = (Array.isArray(names) ? names : [])
    .filter((name) => typeof name === 'string' && !isNumericName(name))
    .map(carrierPrefix)
    .filter((carrier) => carrier !== '');
  return [...new Set(carriers)].sort((a, b) => a.localeCompare(b, 'pt-BR')).join(', ');
};

export const MISSING_SHORT_NAME_OPTION = 'missing-short-name:';

const hasShortName = (tenant) => Boolean(tenant) && normalizeTrunkSegment(tenant.short_name) !== '';

export const tenantOptionValue = (tenant, index) => (hasShortName(tenant) ? String(tenant.short_name) : `${MISSING_SHORT_NAME_OPTION}${index}`);

export const findTenantByOptionValue = (tenants, value) => {
  if (!Array.isArray(tenants) || !value) return undefined;
  return tenants.find((tenant, index) => tenantOptionValue(tenant, index) === value);
};
